import React, { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";
import ZForm from "@organisms/ZForm";
import {
	EWidgets,
	IFormFieldProps,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { ILogCallUIProps, IRelationOption } from "../interface";
import { ZomentumEntities } from "@/Contracts";
import FormBuilder from "antd-form-builder";
import { defaultCallLogOptions } from "../../../../utils";
import { getNearestPastFiveMinutes, isVendorApp } from "@/Utils";
import range from "lodash/range";
import "../index.less";
import { ParentContext } from "../../../../store/context/ParentContext";

const ZAddLogCall: React.FC<ILogCallUIProps> = ({
	onSubmit,
	onClose,
	form,
	isLoading = false,
	ZOpportunitySelectFormItem,
	ZClientSelectFormItem,
	ZUserSelectFormItem,
	ZVendorPartnerSelectFormItem,
	ZVendorOpportunitySelectFormItem,
	ZVendorClientSelectFormItem,
	isSubmitDisabled = false,
	datePattern,
	handleFormChange,
	isReadOnly,
	isEditMode
}) => {
	const parentContext = useContext(ParentContext);
	const currentDate = new Date();
	const todayStartHour = currentDate.getHours();
	const todayStartMinute = currentDate.getMinutes();
	const todayStartSecond = currentDate.getSeconds();
	const startDate = new Date(new Date().setHours(0, 0, 0, 0));
	const todayStartDateUnixTimeStamp = startDate.getTime();
	const customFieldList = parentContext.setting.customFieldList;
	const [customisedCallOutcomes, setCustomisedCallOutcomes] = useState<
		{ name: string; value: string }[]
	>(defaultCallLogOptions);

	const outcomeList = customFieldList?.find(
		field => field.system_field_id === "call_log_outcome"
	)?.allowed_dropdown_values;

	useEffect(() => {
		const customisedCallOutcomes =
			outcomeList?.map(customField => ({
				name: customField,
				value: customField
			})) ?? [];
		setCustomisedCallOutcomes(customisedCallOutcomes);
		if (!isEditMode) {
			form.setFieldsValue({
				outcome: customisedCallOutcomes?.[0]?.value
			});
		}
	}, [outcomeList]);

	const isVendorApplication = isVendorApp();

	const getRelationOptions = useCallback(() => {
		const relationOptions: IRelationOption[] = [];

		if (isVendorApplication) {
			relationOptions.push({
				label: "Opportunity",
				value: ZomentumEntities.VendorOpportunity,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorOpportunitySelectFormItem
							disabled={isReadOnly}
						/>
					</div>
				)
			});
			relationOptions.push({
				label: "Client",
				value: ZomentumEntities.VendorClient,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorClientSelectFormItem disabled={isReadOnly} />
					</div>
				)
			});
			relationOptions.push({
				label: "Partner",
				value: ZomentumEntities.VendorCompany,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorPartnerSelectFormItem disabled={isReadOnly} />
					</div>
				)
			});
		} else {
			relationOptions.push({
				label: "Opportunity",
				value: ZomentumEntities.Opportunity,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZOpportunitySelectFormItem disabled={isReadOnly} />
					</div>
				)
			});
			relationOptions.push({
				label: "Client",
				value: ZomentumEntities.Client,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZClientSelectFormItem disabled={isReadOnly} />
					</div>
				)
			});
		}
		return relationOptions;
	}, [
		isVendorApp,
		ZVendorPartnerSelectFormItem,
		ZOpportunitySelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZClientSelectFormItem
	]);

	const validateTime = (time: moment.Moment | null) => {
		if (!time) return;
		time.isAfter(getNearestPastFiveMinutes(currentDate))
			? form.setFieldsValue({
					time: getNearestPastFiveMinutes(currentDate)
			  })
			: form.setFieldsValue({
					time: getNearestPastFiveMinutes(time.toDate())
			  });
	};

	const onTimeChangeHandler = (newValue: moment.Moment | null) => {
		validateTime(newValue);
	};

	const DateAndTime = () => (
		<FormBuilder
			form={form}
			disabled={isLoading || isReadOnly}
			meta={{
				columns: 2,
				fields: [
					{
						key: "date",
						required: true,
						message: "Please enter date",
						wrapperCol: { span: 15 },
						widget: EWidgets.DATEPICKER,
						widgetProps: {
							disabled: isReadOnly,
							datePattern,
							allowClear: false,
							placeholder: "Date",
							className: "w-full",
							onChange: (
								date: moment.Moment | null,
								_dateString: string
							) => {
								const time = form.getFieldValue(
									"time"
								) as moment.Moment;
								if (date && time) {
									form.setFields([
										{
											name: "time",
											value:
												date.format("MM-DD-YYYY") ===
												moment(currentDate).format(
													"MM-DD-YYYY"
												)
													? getNearestPastFiveMinutes(
															currentDate
													  )
													: getNearestPastFiveMinutes(
															date.toDate()
													  )
										}
									]);
								}
							},
							disabledDate: (date: moment.Moment) =>
								date
									? date.toDate().getTime() >=
									  todayStartDateUnixTimeStamp +
											24 * 60 * 60 * 1000
									: false
						},
						hasFeedback: true,
						initialValue: moment()
					},
					{
						key: "time",
						widget: EWidgets.TIMEPICKER,
						wrapperCol: { span: 13 },
						required: true,
						message: "Please enter time",
						widgetProps: {
							disabled: isReadOnly,
							minuteStep: 5,
							allowClear: false,
							className: "w-full",
							format: "HH:mm",
							placeholder: "Time",
							hideDisabledOptions: true,
							disabledHours: () => {
								const date = form.getFieldValue(
									"date"
								) as moment.Moment;
								return date &&
									date.unix() * 1000 >=
										todayStartDateUnixTimeStamp
									? range(todayStartHour + 1, 24, 1)
									: new Array<number>();
							},
							disabledMinutes: (selectedHour: number) => {
								const date = form.getFieldValue(
									"date"
								) as moment.Moment;
								return date &&
									date.unix() * 1000 >=
										todayStartDateUnixTimeStamp &&
									selectedHour >= todayStartHour
									? range(todayStartMinute, 60, 1)
									: new Array<number>();
							},
							disabledSeconds: (
								selectedHour: number,
								selectedMinute: number
							) => {
								const date = form.getFieldValue(
									"date"
								) as moment.Moment;
								return date &&
									date.unix() * 1000 >=
										todayStartDateUnixTimeStamp &&
									selectedHour >= todayStartHour &&
									selectedMinute >= todayStartMinute
									? range(todayStartSecond, 60, 1)
									: new Array<number>();
							},
							onOk: validateTime,
							onChange: onTimeChangeHandler
						},
						hasFeedback: true,
						initialValue: getNearestPastFiveMinutes(currentDate)
					}
				]
			}}
		/>
	);

	const sections: Array<IFormSectionProps> = [
		{
			isIndentFormItems: false,
			hideDivider: true,
			getMeta: () => {
				const fields: IFormFieldProps[] = [
					{
						key: "title",
						label: "Title",
						widget: EWidgets.INPUT,
						widgetProps: {
							width: "full",
							placeholder: "Please enter title",
							disabled: isReadOnly
						},
						required: true,
						message: "Please enter title",
						hasFeedback: true,
						rules: [
							{
								whitespace: true,
								message: "Title cannot be empty"
							}
						]
					},
					{
						key: "description",
						label: "Description",
						widget: EWidgets.INPUTTEXTAREA,
						widgetProps: {
							placeholder: "Please enter description",
							autoSize: { minRows: 3, maxRows: 6 },
							allowClear: true,
							disabled: isReadOnly
						}
					},
					{
						key: "custom_date",
						label: "Date",
						required: true,
						initialValue: moment(),
						widget: DateAndTime
					}
				];
				if (!isReadOnly) {
					fields.push({
						key: "relation",
						label: "Related to",
						widget: EWidgets.RADIOGROUP,
						widgetProps: {
							className: "sales-activity-relation-radio",
							space: "middle",
							options: getRelationOptions(),
							disabled: isReadOnly
						},
						required: true,
						message: "Please select relation"
					});
				}
				fields.push({
					key: "outcome",
					label: "Outcome",
					required: true,
					message: "Please select outcome",
					hasFeedback: true,
					widget: EWidgets.SELECT,
					widgetProps: {
						allowClear: false,
						"data-testid": "outcome-select",
						placeholder: "Please select outcome",
						selectOptions: customisedCallOutcomes,
						disabled: isReadOnly
					}
				});
				if (!isReadOnly) {
					fields.push({
						key: "author_id",
						render: () => (
							<ZUserSelectFormItem
								showAssociatedUsers={
									isEditMode ? isReadOnly : true
								}
								disabled={isReadOnly}
							/>
						)
					});
				}
				return {
					formItemLayout: {
						labelCol: { span: 5 },
						wrapperCol: { span: 19 }
					},
					fields
				};
			}
		}
	];

	const formProps: IFormProps = {
		key: "AddLogCallForm",
		form,
		sections,
		disabled: isLoading || isReadOnly,
		onFinish: onSubmit,
		submitButtonText: "Save",
		isRightFooter: true,
		isStickFooter: true,
		isCancelAllowed: true,
		onCancel: onClose,
		isSubmitDisabled: isSubmitDisabled,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange,
		formFooter: isReadOnly ? <div /> : undefined
	};

	return <ZForm {...formProps} className="py-4" />;
};

export default ZAddLogCall;
